// src/components/Journal.js
import QuickCaptureModal from './QuickCaptureModal'
import FullScreenCamera from './FullScreenCamera'
import { saveMoment } from '../momentsService'
import CompletionJourney from './CompletionJourney'
import RitualScreen from './RitualScreen'
import CaptureIcon from './CaptureIcon'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../supabaseClient'
import * as content from '../data/content'
import shareImage from '../utils/shareImage'
import Header from './ui/Header'
import PrimaryButton from './ui/PrimaryButton'
const { CITIES } = content

/* ── Localization ── */
const t = (city, strings) => strings[city] || strings.nyc || Object.values(strings)[0]

const GRAT_PLACEHOLDER = {
  nyc: 'I am grateful for…', paris: 'Je suis reconnaissant pour…',
  london: 'I am grateful for…', milan: 'Sono grato per…',
  berlin: 'Ich bin dankbar für…', madrid: 'Estoy agradecido por…',
}

const PROMPTS = {
  nyc: [
    'What are you grateful for?',
    'Who made your day better recently?',
    'What small thing brought you joy today?',
    'What ability or strength are you thankful for?',
    'What place are you grateful to have access to?',
    'What memory fills you with warmth?',
    'What challenge taught you something valuable?',
    'Who in your life deserves more appreciation?',
    'What part of your body are you thankful for today?',
    'What opportunity are you grateful to have right now?',
  ],
  paris: [
    'Pour quoi êtes-vous reconnaissant?',
    'Qui a rendu votre journée meilleure?',
    'Quelle petite chose vous a apporté de la joie?',
    'Pour quelle capacité êtes-vous reconnaissant?',
    'Quel lieu appréciez-vous d\'avoir?',
    'Quel souvenir vous réchauffe le cœur?',
    'Quel défi vous a appris quelque chose?',
    'Qui mérite plus d\'appréciation?',
    'Pour quelle partie de votre corps êtes-vous reconnaissant?',
    'Quelle opportunité avez-vous en ce moment?',
  ],
  milan: [
    'Per cosa sei grato?',
    'Chi ha migliorato la tua giornata?',
    'Quale piccola cosa ti ha portato gioia?',
    'Per quale capacità sei grato?',
    'Quale luogo sei grato di avere?',
    'Quale ricordo ti riscalda il cuore?',
    'Quale sfida ti ha insegnato qualcosa?',
    'Chi merita più apprezzamento?',
    'Per quale parte del tuo corpo sei grato oggi?',
    'Quale opportunità hai in questo momento?',
  ],
}
PROMPTS.london = PROMPTS.nyc

/* ── Confetti ── */
function fireConfetti(color) {
  const canvas = document.createElement('canvas')
  canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999;'
  document.body.appendChild(canvas)
  const ctx = canvas.getContext('2d')
  canvas.width = window.innerWidth; canvas.height = window.innerHeight
  const COLORS = [color, '#FCCC0A', '#00933C', '#0039A6', '#FF6319', '#B933AD']
  const particles = Array.from({ length: 130 }, () => ({
    x: Math.random() * canvas.width, y: -10,
    w: Math.random() * 10 + 4, h: Math.random() * 6 + 3,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    vx: (Math.random() - 0.5) * 4, vy: Math.random() * 4 + 2,
    rot: Math.random() * 360, rotV: (Math.random() - 0.5) * 6, opacity: 1,
  }))
  let frame = 0
  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    particles.forEach(p => {
      p.x += p.vx; p.y += p.vy; p.rot += p.rotV
      if (frame > 80) p.opacity -= 0.015
      ctx.save(); ctx.globalAlpha = Math.max(0, p.opacity)
      ctx.translate(p.x, p.y); ctx.rotate(p.rot * Math.PI / 180)
      ctx.fillStyle = p.color; ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h)
      ctx.restore()
    })
    frame++
    if (frame < 140) requestAnimationFrame(animate)
    else document.body.removeChild(canvas)
  }
  requestAnimationFrame(animate)
}

/* ── Metro icon ── */
function JournalMetroIcon({ city }) {
  const sz = 46
  const base = { width:sz, height:sz, borderRadius:'50%', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', flexShrink:0, boxShadow:'0 3px 10px rgba(0,0,0,0.4),inset 0 -2px 4px rgba(0,0,0,0.2)' }
  const gloss = { position:'absolute', inset:0, borderRadius:'50%', background:'radial-gradient(ellipse 72% 58% at 36% 26%,rgba(255,255,255,0.5) 0%,rgba(255,255,255,0.12) 42%,rgba(255,255,255,0) 68%)', pointerEvents:'none', zIndex:10 }
  const mP = 'M212.25,205.15V81.5c0-7.09-3.9-14.53-15.6-14.53-8.85,0-12.4,3.9-16.29,11.69l-38.27,79.73h-.35L103.11,78.66C99.21,70.87,95.67,67,86.81,67,75.12,67,71.22,74.41,71.22,81.5V205.15c0,6.74,5.32,10.64,11.7,10.64,5.66,0,12-3.9,12-10.64V113h.36L130.4,184.6c2.47,5,5.67,7.8,11.34,7.8s8.85-2.84,11.33-7.8L188.15,113h.36v92.12c0,6.74,6.37,10.64,12.05,10.64,6.37,0,11.69-3.9,11.69-10.64'
  const sv = sz * 0.65
  if (city === 'nyc') return <div style={{...base,background:'#111'}}><div style={gloss}/><svg width={sv} height={sv} viewBox="0 0 284 284" style={{position:'relative',zIndex:2}}><path fill="#fff" d={mP}/></svg></div>
  if (city === 'paris') return <div style={{...base,background:'#fff'}}><div style={gloss}/><svg width={sz} height={sz} viewBox="0 0 284 284" style={{position:'absolute',inset:0,zIndex:2}}><circle cx="142" cy="142" r="133" fill="#fff" stroke="#0A0082" strokeWidth="18"/><path fill="#0A0082" d={mP}/></svg></div>
  if (city === 'london') return <div style={{...base,background:'#E32017'}}><div style={gloss}/><svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`} style={{position:'absolute',inset:0,zIndex:2}}><rect x="0" y={sz*0.38} width={sz} height={sz*0.24} fill="#003087"/><text x={sz/2} y={sz*0.545} textAnchor="middle" fontFamily="Helvetica Neue,Arial,sans-serif" fontWeight="900" fontSize={sz*0.1} fill="#fff">UNDERGROUND</text></svg></div>
  if (city === 'milan') return <div style={{...base,background:'#CC0000'}}><div style={gloss}/><svg width={sv*0.78} height={sv*0.78} viewBox="0 0 110 110" style={{position:'relative',zIndex:2}}><line x1="12" y1="50" x2="12" y2="8" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="50" x2="98" y2="8" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="12" y1="8" x2="55" y2="44" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="8" x2="55" y2="44" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><rect x="0" y="49" width="110" height="12" fill="#CC0000"/><line x1="12" y1="61" x2="12" y2="102" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="61" x2="98" y2="102" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="12" y1="102" x2="55" y2="66" stroke="#fff" strokeWidth="16" strokeLinecap="round"/><line x1="98" y1="102" x2="55" y2="66" stroke="#fff" strokeWidth="16" strokeLinecap="round"/></svg></div>
  if (city === 'berlin') return <div style={{...base,background:'#F0CA00'}}><div style={gloss}/><svg width={sv*0.72} height={sv*0.72} viewBox="0 0 100 100" style={{position:'relative',zIndex:2}}><text x="50" y="68" textAnchor="middle" fontFamily="Helvetica Neue,Arial,sans-serif" fontWeight="900" fontSize="72" fill="#000">U</text></svg></div>
  if (city === 'madrid') return <div style={{...base,background:'#E4032E'}}><div style={gloss}/><svg width={sv*0.72} height={sv*0.72} viewBox="0 0 100 100" style={{position:'relative',zIndex:2}}><text x="50" y="68" textAnchor="middle" fontFamily="Helvetica Neue,Arial,sans-serif" fontWeight="900" fontSize="68" fill="#fff">M</text></svg></div>
  return null
}

/* ══════════════════════════════════════════
   MAIN JOURNAL COMPONENT
   view: 'stops' | 'practice' | 'arrival' | 'grid'
══════════════════════════════════════════ */
export default function Journal({ session, city, theme, toggleTheme, onBack, onShowDashboard, onShowLines, onProgressUpdate, onRound2ProgressUpdate, onShowMoments, qaStation, journalMode = 'first_pass' }) {
  const cityData = CITIES[city]
  const days = (content.DAYS || {})[city]

  const [entries, setEntries] = useState({})
  const [syncing, setSyncing] = useState(true)
  const [view, setView] = useState('stops')
  const [completedStation, setCompletedStation] = useState(null) // station just finished
  const [shareGrat, setShareGrat] = useState(null) // { text, stationNum, city }
  const cardRef = React.useRef(null)
  const [shareCopied, setShareCopied] = useState(false)
  const [generatingImage, setGeneratingImage] = useState(false)
  const [momentPhoto, setMomentPhoto] = useState(null) // base64 photo for this arrival
  const [momentSaved, setMomentSaved] = useState(false)
  const [momentCaption, setMomentCaption] = useState('')
  const [quickCapture, setQuickCapture] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [capturedPhotos, setCapturedPhotos] = useState({}) // { stopIndex: { url, text, location } }
  const [captureForStop, setCaptureForStop] = useState(null)
  const [showCompletion, setShowCompletion] = useState(false)
  const [arrivalHeaderVisible, setArrivalHeaderVisible] = useState(false)

  /* ── Current station ── */
  const currentStation = React.useMemo(() => {
    if (qaStation) return qaStation
    const done = Object.keys(entries).filter(k => entries[k]?.gratitudes?.some(g => g?.trim())).map(Number)
    if (done.length === 0) return 1
    return Math.min(Math.max(...done) + 1, 28)
  }, [entries, qaStation])

  /* ── Stop state ── */
  const TOTAL_STOPS = 10
  const [stopIndex, setStopIndex] = useState(0)
  const [gratitudes, setGratitudes] = useState(Array(10).fill(''))
  const [gratThanks, setGratThanks] = useState(Array(10).fill(0))
  const [typed, setTyped] = useState(false)
  const [breathing, setBreathing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const toastRef = useRef(null)
  const wrapRef = useRef(null)

  /* ── Load entries ── */
  const loadEntries = useCallback(async () => {
    if (!session) { setSyncing(false); return }
    setSyncing(true)
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('city', city)
    if (!error && data) {
      const map = {}
      data.forEach(row => { map[row.day_number] = row })
      setEntries(map)
    }
    setSyncing(false)
  }, [session?.user?.id, city])

  useEffect(() => { loadEntries() }, [loadEntries])

  useEffect(() => {
    if (view === 'arrival') fireConfetti(cityData.primaryColor)
  }, [view]) // eslint-disable-line

  useEffect(() => {
    if (view !== 'arrival') { setArrivalHeaderVisible(false); return }
    const id = setTimeout(() => setArrivalHeaderVisible(true), 2000)
    return () => clearTimeout(id)
  }, [view])

  useEffect(() => {
    document.documentElement.style.setProperty('--city-primary', cityData.primaryColor)
    const hex = cityData.primaryColor.replace('#', '')
    const r = parseInt(hex.substring(0,2),16), g = parseInt(hex.substring(2,4),16), b = parseInt(hex.substring(4,6),16)
    document.documentElement.style.setProperty('--city-primary-rgb', `${r},${g},${b}`)
    return () => {
      document.documentElement.style.removeProperty('--city-primary')
      document.documentElement.style.removeProperty('--city-primary-rgb')
    }
  }, [cityData])

  const showToast = (msg) => {
    setToast(msg)
    clearTimeout(toastRef.current)
    toastRef.current = setTimeout(() => setToast(null), 2800)
  }

  const isDone = (data) => data?.gratitudes?.some(g => g?.trim())
  const doneCount = Object.values(entries).filter(d => isDone(d)).length
  const stationData = days?.[currentStation - 1]

  // Streak = longest consecutive run from station 1 (never resets)
  const streak = React.useMemo(() => {
    let s = 0
    for (let i = 1; i <= 28; i++) {
      if (isDone(entries[i])) s = i
      else break
    }
    return s
  }, [entries])

  // Memory resurfacing: pick a random past gratitude to show
  const memoryGrat = React.useMemo(() => {
    const past = []
    Object.entries(entries).forEach(([day, data]) => {
      if (Number(day) < currentStation && data?.gratitudes) {
        data.gratitudes.forEach(g => {
          if (g?.trim()) past.push({ text: g, day: Number(day) })
        })
      }
    })
    if (past.length === 0) return null
    // Only show ~30% of the time, seeded by station number
    if ((currentStation * 7) % 10 > 2) return null
    return past[Math.floor(Math.random() * past.length)]
  }, [entries, currentStation])

  // Milestone detection for arrival screen
  const isMilestone = (n) => [7, 14, 21, 28].includes(n)
  const MILESTONE_LABELS = {
    nyc:   { 7: 'One Week In', 14: 'Halfway There', 21: 'Three Weeks Strong', 28: 'Journey Complete' },
    paris: { 7: 'Une Semaine', 14: 'À Mi-Chemin',   21: 'Trois Semaines',     28: 'Voyage Accompli' },
    milan: { 7: 'Una Settimana', 14: 'A Metà Strada', 21: 'Tre Settimane',    28: 'Viaggio Completo' },
  }
  MILESTONE_LABELS.london = MILESTONE_LABELS.nyc
  const prompts = PROMPTS[city] || PROMPTS.nyc

  /* ── Breath transition ── */
  const slideToNext = (fn) => {
    const el = wrapRef.current
    if (el) {
      el.style.transition = 'all 0.25s ease'
      el.style.opacity = '0'
      el.style.transform = 'translateY(8px)'
    }
    setTimeout(() => {
      fn()
      setBreathing(true)
    }, 250)
    setTimeout(() => {
      setBreathing(false)
      if (el) { el.style.opacity = '1'; el.style.transform = 'translateY(0)' }
    }, 650)
  }

  const tapThanks = (idx) => {
    const t2 = [...gratThanks]
    if (t2[idx] >= 3) return
    t2[idx] = t2[idx] + 1
    setGratThanks(t2)
  }

  /* ── Next stop / save ── */
  const handleNext = async () => {
    if (stopIndex < TOTAL_STOPS - 1) {
      slideToNext(() => {
        setStopIndex(s => s + 1)
        setTyped(false)
      })
    } else {
      setSaving(true)
      if (session) {
        const now = new Date().toISOString()

        if (journalMode === 'first_pass') {
          const existing = entries[currentStation]
          const payload = {
            user_id: session.user.id, city,
            day_number: currentStation,
            gratitudes, grat_thanks: gratThanks,
            journal: '', mood: [], images: [],
            updated_at: now,
            completed_at: existing?.completed_at || now,
          }
          const { error } = await supabase
            .from('journal_entries')
            .upsert(payload, { onConflict: 'user_id,city,day_number' })

          if (error) { showToast('Error saving — try again'); setSaving(false); return }

          setEntries(prev => ({ ...prev, [currentStation]: payload }))
          if (onProgressUpdate) onProgressUpdate(city, currentStation)

        } else {
          // 'practice' or 'round2' — append only, never touches journal_entries
          const { error } = await supabase
            .from('practice_sessions')
            .insert({
              user_id: session.user.id,
              city,
              station: currentStation,
              session_type: journalMode === 'round2' ? 'round2' : 'practice',
              round: journalMode === 'round2' ? 2 : 1,
              gratitudes,
              grat_thanks: gratThanks,
              images: [],
              journal: '',
              mood: [],
              completed_at: now,
            })

          if (error) { showToast('Error saving — try again'); setSaving(false); return }

          if (journalMode === 'round2') {
            await supabase
              .from('round_2_progress')
              .upsert(
                { user_id: session.user.id, city, current_station: currentStation },
                { onConflict: 'user_id,city' }
              )
            if (onRound2ProgressUpdate) onRound2ProgressUpdate(city, currentStation)
          }
        }
      }
      setSaving(false)
      setCompletedStation(currentStation)
      setTimeout(() => setView('ritual'), 400)
    }
  }

  const hasText = gratitudes[stopIndex]?.trim().length > 2
  const isReady = (typed || hasText) && gratThanks[stopIndex] >= 3

  /* ══════════════
     STOPS VIEW
  ══════════════ */
  if (view === 'stops') {
    const pad = n => n < 10 ? '0' + n : '' + n
    const progress = ((stopIndex + 1) / TOTAL_STOPS) * 100

    return (
      <div className="journal-stops-page" data-city={city}>
        {breathing && (
          <div className="stops-breath-overlay">
            <div className="stops-breath-mark" style={{ background: cityData.primaryColor }}>✦</div>
          </div>
        )}

        <Header
          back={{ label: cityData.name, onClick: onBack }}
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cityData.primaryColor, display: 'inline-block', flexShrink: 0 }} />
              {t(city, { nyc: 'Station', paris: 'Station', milan: 'Fermata', berlin: 'Haltestelle', madrid: 'Parada' })} {currentStation}
            </span>
          }
          action={
            <button className="stops-header-theme" onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌑'}
            </button>
          }
        />

        {/* ── Single slim progress bar — gratitude 1 of 10 ── */}
        <div className="stops-progress-bar-wrap">
          <div
            className="stops-progress-bar-fill"
            style={{
              width: `${((stopIndex + 1) / TOTAL_STOPS) * 100}%`,
              background: cityData.primaryColor,
            }}
          />
        </div>

        <div className="stops-wrap" ref={wrapRef}>
          <div className="stops-context">
            <p className="stops-eyebrow" style={{ color: cityData.primaryColor }}>
              {stationData?.title}
            </p>
          </div>

          <div
            className={'stops-big-num' + (typed ? ' active' : '')}
            style={{ '--city': cityData.primaryColor }}
          >
            {pad(stopIndex + 1)}
          </div>

          {/* Memory resurfacing — only on first stop */}
          {stopIndex === 0 && memoryGrat && (
            <div className="stops-memory-card">
              <span className="stops-memory-label">
                {t(city, { nyc: 'From your journey', paris: 'De votre voyage', milan: 'Dal tuo viaggio' })}
                {' · '}Station {memoryGrat.day}
              </span>
              <p className="stops-memory-text">"{memoryGrat.text}"</p>
            </div>
          )}

          <p className="stops-prompt">{prompts[stopIndex]}</p>

          {/* Inline moment card if captured for this stop */}
          {capturedPhotos[stopIndex] && (
            <div className="stops-moment-card">
              <img src={capturedPhotos[stopIndex].url} alt="" className="stops-moment-img" />
              <div className="stops-moment-info">
                <div className="stops-moment-badge" style={{ background: cityData.primaryColor }}>
                  {t(city, { nyc: 'Moment captured', paris: 'Moment capturé', milan: 'Momento catturato', berlin: 'Moment erfasst', madrid: 'Momento capturado' })}
                </div>
                {capturedPhotos[stopIndex].location && (
                  <div className="stops-moment-loc">
                    <svg width="8" height="11" viewBox="0 0 14 18" fill="none"><path d="M7 0C3.13 0 0 3.13 0 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5C5.62 9.5 4.5 8.38 4.5 7S5.62 4.5 7 4.5 9.5 5.62 9.5 7 8.38 9.5 7 9.5z" fill={cityData.primaryColor}/></svg>
                    {capturedPhotos[stopIndex].location}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder={GRAT_PLACEHOLDER[city]}
              value={gratitudes[stopIndex]}
              onChange={e => {
                const g = [...gratitudes]; g[stopIndex] = e.target.value; setGratitudes(g)
                setTyped(e.target.value.trim().length > 2)
              }}
            />
          </div>

          {/* Full-width capture CTA — only shows if no photo captured yet for this stop */}
          {!capturedPhotos[stopIndex] && (
            <button
              type="button"
              className="stops-capture-cta"
              onClick={e => { e.preventDefault(); e.stopPropagation(); setCaptureForStop(stopIndex); setShowCamera(true) }}
            >
              <CaptureIcon color={cityData.primaryColor} size={16} />
              <span className="stops-capture-cta-text">CAPTURE THE MOMENT</span>
            </button>
          )}

          <div className="stops-ty-section">
            <p className="stops-ty-label">
              {gratThanks[stopIndex] === 0 && t(city, { nyc: 'Now say thank you — three times', paris: 'Maintenant dites merci — trois fois', milan: 'Ora dì grazie — tre volte', berlin: 'Jetzt danke sagen — dreimal', madrid: 'Ahora di gracias — tres veces' })}
              {gratThanks[stopIndex] === 1 && t(city, { nyc: 'Keep going…', paris: 'Continuez…', milan: 'Continua…' })}
              {gratThanks[stopIndex] === 2 && t(city, { nyc: 'One more…', paris: 'Encore une…', milan: 'Ancora uno…' })}
              {gratThanks[stopIndex] >= 3 && t(city, { nyc: 'Beautiful. ✦', paris: 'Magnifique. ✦', milan: 'Bellissimo. ✦' })}
            </p>
            <div className="stops-ty-btns">
              {[0, 1, 2].map(i => (
                <button
                  key={i}
                  className={'stops-ty-btn' + (gratThanks[stopIndex] > i ? ' tapped' : '')}
                  style={gratThanks[stopIndex] > i ? { background: cityData.primaryColor, borderColor: cityData.primaryColor } : {}}
                  onClick={() => (typed || hasText) && tapThanks(stopIndex)}
                >
                  <span className="stops-ty-sym">✦</span>
                  <span className="stops-ty-word">{t(city, { nyc: 'Thank you', paris: 'Merci', milan: 'Grazie', berlin: 'Danke', madrid: 'Gracias' })}</span>
                </button>
              ))}
            </div>
          </div>

          <PrimaryButton
            onClick={isReady ? handleNext : undefined}
            disabled={saving}
            style={isReady
              ? { background: 'var(--text)', color: 'var(--bg)' }
              : { background: 'var(--border)', color: 'var(--text4)', cursor: 'default' }
            }
          >
            <span>
              {saving
                ? t(city, { nyc: 'Saving…', paris: 'Sauvegarde…', milan: 'Salvataggio…' })
                : stopIndex < TOTAL_STOPS - 1
                  ? t(city, { nyc: 'Next Stop', paris: 'Prochain Arrêt', milan: 'Prossima Fermata', berlin: 'Nächste Haltestelle', madrid: 'Próxima Parada' })
                  : t(city, { nyc: 'Arrive at Station', paris: 'Arriver à la Station', milan: 'Arrivare alla Stazione', berlin: 'Ankunft am Bahnhof', madrid: 'Llegar a la Estación' })
              }
            </span>
            <span>→</span>
          </PrimaryButton>
        </div>

        {toast && <div className="toast show">{toast}</div>}

        {quickCapture && (
          <QuickCaptureModal
            session={session}
            currentCity={city}
            defaultDay={currentStation}
            allEntries={{ [city]: entries }}
            hideStationPicker={true}
            onClose={() => setQuickCapture(false)}
            onSaved={() => setQuickCapture(false)}
          />
        )}

        {showCamera && (
          <FullScreenCamera
            cityColor={cityData.primaryColor}
            onClose={(reason) => {
              setShowCamera(false)
              if (reason === 'picker') setQuickCapture(true)
            }}
            onCapture={async (dataUrl) => {
              setShowCamera(false)
              const stopIdx = captureForStop ?? stopIndex
              await saveMoment({
                userId: session?.user?.id,
                imageUrl: dataUrl,
                text: gratitudes[stopIdx]?.trim() || '',
                city,
                dayNumber: currentStation,
              })
              setCapturedPhotos(prev => ({
                ...prev,
                [stopIdx]: { url: dataUrl, location: null }
              }))
              if (!gratitudes[stopIdx]?.trim()) {
                const g = [...gratitudes]
                g[stopIdx] = ''
                setGratitudes(g)
              }
              setTyped(true)
            }}
          />
        )}
      </div>
    )
  }


/* ══════════════
     SHARE CARD MODAL
  ══════════════ */
  if (shareGrat) {
    const sc = shareGrat
    const shareText = '"' + sc.text + '"\n\nStation ' + sc.stationNum + ' · ' + sc.cityData.name + ' Line\nThe Magic Journey — magic.soappower.com'


    const handleCopy = () => {
      navigator.clipboard?.writeText(shareText).then(() => {
        setShareCopied(true)
        setTimeout(() => setShareCopied(false), 2500)
      })
    }

    const captureCard = async () => {
      // 1st try: html2canvas screenshot
      try {
        const el = cardRef.current
        if (!el) throw new Error('no ref')
        const h2c = await new Promise((resolve) => {
          if (window.html2canvas) { resolve(window.html2canvas); return }
          const s = document.createElement('script')
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
          s.onload = () => resolve(window.html2canvas)
          s.onerror = () => resolve(null)
          document.head.appendChild(s)
        })
        if (!h2c) throw new Error('html2canvas failed to load')
        const canvas = await h2c(el, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: null, logging: false })
        if (canvas) return canvas
      } catch(e) { console.warn('html2canvas failed, using fallback:', e.message) }

      // 2nd try: solid color fallback — always works
      try {
        const W = 1080, H = 720
        const canvas = document.createElement('canvas')
        canvas.width = W; canvas.height = H
        const ctx = canvas.getContext('2d')
        const color = sc.cityData.primaryColor || '#c00'
        ctx.fillStyle = color; ctx.fillRect(0, 0, W, H)
        const grad = ctx.createLinearGradient(0, 0, 0, H)
        grad.addColorStop(0, 'rgba(0,0,0,0.3)'); grad.addColorStop(1, 'rgba(0,0,0,0.75)')
        ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H)
        ctx.fillStyle = '#fff'; ctx.textAlign = 'left'
        ctx.font = 'bold 28px Arial, sans-serif'; ctx.fillText('✦  THE MAGIC JOURNEY', 64, 72)
        ctx.font = '20px Arial, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.6)'
        ctx.fillText((sc.cityData.name || '').toUpperCase() + '  ·  STATION ' + sc.stationNum, 64, 112)
        ctx.fillStyle = color; ctx.fillRect(64, 128, 240, 3)
        ctx.fillStyle = '#fff'; ctx.font = 'italic bold 52px Arial, sans-serif'; ctx.textAlign = 'center'
        const words = ('"' + sc.text + '"').split(' '); const lines = []; let line = ''
        for (const word of words) {
          const test = line ? line + ' ' + word : word
          if (ctx.measureText(test).width > W - 128 && line) { lines.push(line); line = word } else line = test
        }
        if (line) lines.push(line)
        lines.forEach((l, i) => { ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 12; ctx.fillText(l, W/2, H * 0.42 + i * 68) })
        ctx.shadowBlur = 0; ctx.font = '20px Arial, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.4)'
        ctx.fillText('magic.soappower.com', W/2, H - 40)
        return canvas
      } catch(e) { return null }
    }
    const handleShare = async () => {
      setGeneratingImage(true)
      let blob = null
      try {
        const canvas = await captureCard()
        if (canvas) blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92))
      } catch (e) {

      }

      setGeneratingImage(false)

      if (!blob) {
        if (navigator.share) { try { await navigator.share({ title: 'The Magic Journey', text: shareText }) } catch(e) {} }
        else { handleCopy() }
        return
      }

      await shareImage(blob, 'magic-postcard.jpg', shareText + '\n\nmagic.soappower.com')
    }

    return (
      <div className="share-modal-page" data-city={city}>
        <div className="share-modal-wrap">
          <div ref={cardRef} className="share-card" style={{ '--share-city': sc.cityData.primaryColor }}>
            <div className="share-card-stripe" style={{ background: sc.cityData.primaryColor }} />
            <div className="share-card-eyebrow">
              <div className="share-card-bullet" style={{ background: sc.cityData.primaryColor }}>
                {sc.stationNum}
              </div>
              <span>{sc.cityData.name} Line · Station {sc.stationNum}</span>
            </div>
            <p className="share-card-quote">"{sc.text}"</p>
            <div className="share-card-footer">
              <span className="share-card-brand">The Magic Journey</span>
              <span className="share-card-mark" style={{ color: sc.cityData.primaryColor }}>✦</span>
            </div>
          </div>

          <p className="share-hint">
            {t(city, { nyc: 'Share to WhatsApp, Instagram, or anywhere', paris: 'Partager sur WhatsApp ou Instagram', milan: 'Condividi su WhatsApp o Instagram' })}
          </p>

          <div className="share-actions">
            <button className="share-btn-primary" style={{ background: sc.cityData.primaryColor }} onClick={handleShare} disabled={generatingImage}>
              {generatingImage ? '⏳ Creating…' : t(city, { nyc: '↑ Share as Image', paris: '↑ Partager comme image', milan: '↑ Condividi immagine' })}
            </button>
            <button className="share-btn-secondary" onClick={handleCopy}>
              {shareCopied ? '✓ Copied!' : t(city, { nyc: 'Copy text instead', paris: 'Copier le texte', milan: 'Copia il testo' })}
            </button>
            <button className="share-btn-close" onClick={() => { setShareGrat(null); setShareCopied(false) }}>
              ← {t(city, { nyc: 'Back', paris: 'Retour', milan: 'Indietro' })}
            </button>
          </div>
        </div>
        <button className="theme-fab" onClick={toggleTheme}>{theme === 'dark' ? '☀️' : '🌑'}</button>
      </div>
    )
  }

  /* ══════════════
     RITUAL VIEW
  ══════════════ */
  if (view === 'ritual') {
    return (
      <RitualScreen
        day={completedStation || currentStation}
        city={city}
        cityColor={cityData.primaryColor}
        onComplete={() => setView('arrival')}
      />
    )
  }

  /* ══════════════
     ARRIVAL VIEW
  ══════════════ */
  if (view === 'arrival') {
    const arrivedAt = completedStation || currentStation - 1
    const isComplete = arrivedAt >= 28
    const milestone = isMilestone(arrivedAt)
    const milestoneLabel = milestone
      ? (MILESTONE_LABELS[city] || MILESTONE_LABELS.nyc)[arrivedAt]
      : null
    const nextStation = Math.min(arrivedAt + 1, 28)
    const nextStationData = days?.[nextStation - 1]

    return (
      <div className="journal-arrival-page" data-city={city}>
        <div className={`arrival-header-fade${arrivalHeaderVisible ? ' visible' : ''}`}>
          <Header
            back={{ label: 'All Lines', onClick: onBack }}
            action={
              <button className="stops-header-theme" onClick={toggleTheme}>
                {theme === 'dark' ? '☀️' : '🌑'}
              </button>
            }
          />
        </div>
        <div className="journal-arrival-wrap">

          <div className="journal-arrival-metro">
            <JournalMetroIcon city={city} />
          </div>

          {milestone && (
            <div className="arrival-milestone-badge" style={{ background: cityData.primaryColor }}>
              {milestoneLabel}
            </div>
          )}

          <p className="journal-arrival-eyebrow" style={{ color: cityData.primaryColor }}>
            {cityData.name} Line
          </p>

          <h2 className="journal-arrival-title">
            {t(city, { nyc: "You've arrived.", paris: 'Vous êtes arrivé.', milan: 'Sei arrivato.', berlin: 'Du bist angekommen.', madrid: 'Has llegado.' })}
          </h2>

          <p className="journal-arrival-sub">
            {t(city, {
              nyc: `Station ${arrivedAt} complete. You gave thanks 30 times.`,
              paris: `Station ${arrivedAt} complète. Vous avez dit merci 30 fois.`,
              milan: `Stazione ${arrivedAt} completata. Hai detto grazie 30 volte.`,
              berlin: `Station ${arrivedAt} abgeschlossen. Du hast 30 Mal gedankt.`,
              madrid: `Estación ${arrivedAt} completa. Diste gracias 30 veces.`,
            })}
          </p>

          <div className="arrival-streak-wrap">
            <div className="arrival-streak-num" style={{ color: cityData.primaryColor }}>
              {streak}
            </div>
            <div className="arrival-streak-label">
              {t(city, {
                nyc: streak === 1 ? 'station completed' : 'stations in a row',
                paris: streak === 1 ? 'station complétée' : "stations d'affilée",
                milan: streak === 1 ? 'stazione completata' : 'stazioni di fila',
                berlin: streak === 1 ? 'Station abgeschlossen' : 'Stationen hintereinander',
                madrid: streak === 1 ? 'estación completada' : 'estaciones seguidas',
              })}
            </div>
            <div className="arrival-pip-row">
              {Array.from({ length: 28 }, (_, i) => (
                <div
                  key={i}
                  className="arrival-pip"
                  style={{
                    background: i < streak ? (days[i]?.color || cityData.primaryColor) : 'var(--border)',
                    transform: i === arrivedAt - 1 ? 'scaleY(1.8)' : 'scaleY(1)',
                  }}
                />
              ))}
            </div>
          </div>

          {/* ── Arrival Postcard — hero share card ── */}
          {entries[arrivedAt]?.gratitudes?.filter(g => g?.trim()).length > 0 && (() => {
            const grats = (entries[arrivedAt]?.gratitudes || []).filter(g => g?.trim())
            const heroGrat = grats[0]
            const arrivalCardRef = React.createRef()
            return (
              <div className="arrival-postcard-hero">
                <p className="arrival-postcard-label">
                  {t(city, { nyc: 'Your postcard from this station', paris: 'Votre carte de cette station', milan: 'La tua cartolina da questa stazione', berlin: 'Deine Postkarte von dieser Station', madrid: 'Tu postal de esta estación' })}
                </p>
                <div
                  className="db-postcard-card arrival-postcard-card"
                  style={{ background: cityData.primaryColor }}
                  onClick={() => setShareGrat({ text: heroGrat, stationNum: arrivedAt, cityData, photo: getCityPhotoForStation(city, arrivedAt) })}
                >
                  <img
                    src={getCityPhotoForStation(city, arrivedAt)}
                    alt={cityData.name}
                    className="db-postcard-photo"
                    onError={e => { e.target.style.display = 'none' }}
                  />
                  <div className="db-postcard-tint" />
                  <div className="db-postcard-overlay" />
                  <div className="db-postcard-inner">
                    <div className="db-postcard-stamp">
                      <div className="db-postcard-postmark">
                        <span className="db-postcard-postmark-city">{cityData.label || cityData.name}</span>
                        <span className="db-postcard-postmark-mark">✦</span>
                        <span className="db-postcard-postmark-num">{arrivedAt}</span>
                      </div>
                    </div>
                    <div className="db-postcard-quote-block">
                      <p className="db-postcard-quote">"{heroGrat.length > 80 ? heroGrat.slice(0, 80) + '…' : heroGrat}"</p>
                      <div className="db-postcard-meta">
                        <span>{cityData.name}</span>
                        <span className="db-postcard-dot">·</span>
                        <span>Station {arrivedAt}</span>
                      </div>
                    </div>
                  </div>
                  <div className="db-postcard-share-hint">↑ Share</div>
                </div>
              </div>
            )
          })()}

          <div className="journal-arrival-divider" />

          {/* Magic Moment — photo capture */}
          <div className="arrival-moment-wrap">
            <p className="arrival-share-label">
              {t(city, { nyc: 'Capture this moment', paris: 'Capturer ce moment', milan: 'Cattura questo momento', berlin: 'Diesen Moment festhalten', madrid: 'Capturar este momento' })}
            </p>
            {momentPhoto ? (
              <div className="arrival-moment-preview">
                <img src={momentPhoto} alt="Magic moment" className="arrival-moment-img" />
                <textarea
                  className="arrival-moment-caption"
                  placeholder={t(city, { nyc: 'Add a caption… (optional)', paris: 'Ajouter une légende…', milan: 'Aggiungi una didascalia…', berlin: 'Beschriftung hinzufügen…', madrid: 'Añadir un pie de foto…' })}
                  value={momentCaption}
                  onChange={e => setMomentCaption(e.target.value)}
                  rows={2}
                />
              <div className="arrival-moment-actions">
                  <button
                    className="arrival-moment-save-btn"
                    style={{ background: cityData.primaryColor }}
                    disabled={momentSaved}
                    onClick={async () => {
                      const arrivedAt2 = completedStation || currentStation - 1
                      const existing = entries[arrivedAt2]
                      if (!existing) return
                      const updates = { images: [momentPhoto] }
                      if (momentCaption.trim()) {
                        const updatedGrats = [...(existing.gratitudes || [])]
                        updatedGrats[9] = momentCaption.trim() // store caption in last gratitude slot if empty
                        updates.gratitudes = updatedGrats
                      }
                      const { error } = await supabase
                        .from('journal_entries')
                        .update(updates)
                        .eq('user_id', session?.user?.id)
                        .eq('city', city)
                        .eq('day_number', arrivedAt2)
                      if (!error) {
                        setMomentSaved(true)
                        setEntries(prev => ({ ...prev, [arrivedAt2]: { ...prev[arrivedAt2], ...updates } }))
                      }
                    }}
                  >
                    {momentSaved
                      ? t(city, { nyc: '✓ Saved', paris: '✓ Sauvegardé', milan: '✓ Salvato' })
                      : t(city, { nyc: 'Save moment', paris: 'Sauvegarder', milan: 'Salva' })
                    }
                  </button>
                  <button className="arrival-moment-retake" onClick={() => { setMomentPhoto(null); setMomentSaved(false) }}>
                    {t(city, { nyc: 'Retake', paris: 'Reprendre', milan: 'Riprendi' })}
                  </button>
                </div>
              </div>
            ) : (
              <label className="arrival-moment-capture">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    const reader = new FileReader()
                    reader.onload = ev => setMomentPhoto(ev.target.result)
                    reader.readAsDataURL(file)
                  }}
                />
                <div className="arrival-moment-btn" style={{ borderColor: cityData.primaryColor }}>
                  <svg className="arrival-moment-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <span className="arrival-moment-text">
                    {t(city, { nyc: 'Add a photo', paris: 'Ajouter une photo', milan: 'Aggiungi foto', berlin: 'Foto hinzufügen', madrid: 'Añadir foto' })}
                  </span>
                </div>
              </label>
            )}
          </div>

          {/* Share a gratitude */}
          {entries[arrivedAt]?.gratitudes?.length > 0 && (
            <div className="arrival-share-wrap">
              <p className="arrival-share-label">
                {t(city, { nyc: 'Share a gratitude', paris: 'Partager une gratitude', milan: 'Condividi una gratitudine', berlin: 'Dankbarkeit teilen', madrid: 'Compartir una gratitud' })}
              </p>
              <div className="arrival-share-grats">
                {[...new Set((entries[arrivedAt]?.gratitudes || []).filter(g => g?.trim()))].slice(0, 3).map((g, i) => (
                  <button
                    key={i}
                    className="arrival-share-grat-btn"
                    onClick={() => setShareGrat({ text: g, stationNum: arrivedAt, cityData, photo: getCityPhotoForStation(city, arrivedAt) })}
                  >
                    <span className="arrival-share-grat-text">{g.length > 60 ? g.slice(0, 60) + '…' : g}</span>
                    <span className="arrival-share-grat-arrow">↗</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isComplete && nextStationData && (
            <div className="arrival-next-preview">
              <span className="arrival-next-label">
                {t(city, { nyc: 'Next up', paris: 'Ensuite', milan: 'Prossima' })}
              </span>
              <span className="arrival-next-name">
                Station {nextStation} · {nextStationData.title}
              </span>
            </div>
          )}

          {isComplete ? (
            <>
              <p className="arrival-complete-msg">
                {t(city, {
                  nyc: 'You have completed all 28 stations. 🏆',
                  paris: 'Vous avez complété les 28 stations. 🏆',
                  milan: 'Hai completato tutte le 28 stazioni. 🏆',
                  berlin: 'Du hast alle 28 Stationen abgeschlossen. 🏆',
                  madrid: 'Has completado las 28 estaciones. 🏆',
                })}
              </p>
              <button
                className="journal-arrival-btn"
                style={{ background: cityData.primaryColor }}
                onClick={() => setShowCompletion(true)}
              >
                <span>✦ View Your Journey</span>
                <span>→</span>
              </button>
            </>
          ) : (
            <button
              className="journal-arrival-btn"
              style={{ background: cityData.primaryColor }}
              onClick={onShowLines || onBack}
            >
              <span>{t(city, { nyc: 'View all 28 stations', paris: 'Voir les 28 stations', milan: 'Vedi le 28 stazioni' })}</span>
              <span>→</span>
            </button>
          )}

          {onShowMoments && Object.values(entries).some(e => e?.images?.length > 0) && (
            <button className="journal-arrival-moments-link" onClick={onShowMoments}>
              ✦ {t(city, { nyc: 'View your moments', paris: 'Voir vos moments', milan: 'Vedi i tuoi momenti' })}
            </button>
          )}

        </div>

        {showCompletion && (
          <CompletionJourney
            session={session}
            city={city}
            entries={entries}
            onClose={() => setShowCompletion(false)}
            onSharePassport={(text) => {
              if (navigator.share) navigator.share({ title: 'The Magic Journey', text }).catch(() => {})
              else navigator.clipboard?.writeText(text)
            }}
          />
        )}

        {quickCapture && (
          <QuickCaptureModal
            session={session}
            currentCity={city}
            defaultDay={currentStation}
            allEntries={{ [city]: entries }}
            hideStationPicker={true}
            onClose={() => setQuickCapture(false)}
            onSaved={() => setQuickCapture(false)}
          />
        )}

        {showCamera && (
          <FullScreenCamera
            cityColor={cityData.primaryColor}
            onClose={(reason) => {
              setShowCamera(false)
              if (reason === 'picker') setQuickCapture(true)
            }}
            onCapture={async (dataUrl) => {
              setShowCamera(false)
              const stopIdx = captureForStop ?? stopIndex
              await saveMoment({
                userId: session?.user?.id,
                imageUrl: dataUrl,
                text: gratitudes[stopIdx]?.trim() || '',
                city,
                dayNumber: currentStation,
              })
              setCapturedPhotos(prev => ({
                ...prev,
                [stopIdx]: { url: dataUrl, location: null }
              }))
              if (!gratitudes[stopIdx]?.trim()) {
                const g = [...gratitudes]
                g[stopIdx] = ''
                setGratitudes(g)
              }
              setTyped(true)
            }}
          />
        )}
      </div>
    )
  }

  /* ══════════
     GRID VIEW
  ══════════ */
  return (
    <div className="journal-page" data-city={city}>
      <div className="top-stripe" />

      <Header
        back={{ label: cityData.name, onClick: onBack }}
        action={
          <button className="theme-toggle-btn" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️' : '🌑'}
          </button>
        }
      />

      <div className="journal-brand">
        <div className="header-eyebrow">Rhonda Byrne's</div>
        <div className="journal-title-row">
          <h1 className="journal-title">
            {t(city, { nyc: 'The Magic', paris: 'La Magie', london: 'The Magic', milan: 'La Magia' })}
          </h1>
          <JournalMetroIcon city={city} />
        </div>
        <div className="journal-tagline">
          {cityData.system} · 28 {t(city, { nyc: 'Days', paris: 'Jours', london: 'Days', milan: 'Giorni' })} · {t(city, { nyc: 'Gratitude', paris: 'Gratitude', london: 'Gratitude', milan: 'Gratitudine' })}
        </div>
      </div>

      {/* Progress bar */}
      <div className="prog-wrap">
        <div className="prog-meta">
          <span className="prog-lbl">
            {t(city, { nyc: 'Your Journey', paris: 'Votre Voyage', london: 'Your Journey', milan: 'Il Tuo Viaggio' })}
            {syncing && <span className="syncing-dot">●</span>}
          </span>
          <span className="prog-num">{doneCount} / 28</span>
        </div>
        <div className="prog-track">
          <div className="prog-fill" style={{ width: `${(doneCount / 28 * 100).toFixed(1)}%` }} />
        </div>
        <div className="prog-pips">
          {days.map((day, i) => (
            <div key={i} className="pip" style={{ background: isDone(entries[i+1]) ? day.color : undefined }} />
          ))}
        </div>
      </div>

      {/* Today's station CTA */}
      {doneCount < 28 && (
        <div className="grid-today-cta">
          <button
            className="grid-today-btn"
            style={{ '--city': cityData.primaryColor }}
            onClick={() => {
              setStopIndex(0)
              setGratitudes(Array(10).fill(''))
              setGratThanks(Array(10).fill(0))
              setTyped(false)
              setView('stops')
            }}
          >
            <span>
              {t(city, { nyc: `Continue — Station ${currentStation}`, paris: `Continuer — Station ${currentStation}`, milan: `Continua — Stazione ${currentStation}` })}
            </span>
            <span>→</span>
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="shell">
        <div className="grid-header">
          <span className="grid-lbl">
            {t(city, { nyc: 'All 28 Stations', paris: 'Les 28 Stations', london: 'All 28 Stations', milan: 'Le 28 Stazioni' })}
          </span>
          <button className="streak-stats-btn" onClick={onShowDashboard}>Stats →</button>
        </div>

        <div className="days-grid">
          {days.map((day, i) => {
            const n = i + 1
            const data = entries[n]
            const complete = isDone(data)
            const gratCount = (data?.gratitudes || []).filter(g => g?.trim()).length
            const isNext = n === currentStation && !complete
            const isFuture = n > currentStation
            const isLocked = n > currentStation + 1

            const cardState = complete ? 'done' : isNext ? 'next' : isFuture ? 'future' : 'done'

            return (
              <div
                key={n}
                className={`day-card-v2 state-${cardState}`}
                style={{ '--day-color': day.color, '--city': cityData.primaryColor }}
                onClick={(complete || isNext) ? () => {
                  if (isNext) {
                    setStopIndex(0)
                    setGratitudes(Array(10).fill(''))
                    setGratThanks(Array(10).fill(0))
                    setTyped(false)
                    setView('stops')
                  }
                } : undefined}
              >
                {/* Left accent bar */}
                <div className="card-v2-bar" style={{ background: complete ? day.color : isNext ? cityData.primaryColor : 'transparent' }} />

                {/* Station number */}
                <div className="card-v2-num" style={{ color: complete ? day.color : isNext ? cityData.primaryColor : 'var(--text5)' }}>
                  {String(n).padStart(2, '0')}
                </div>

                {/* Content */}
                <div className="card-v2-content">
                  <div className="card-v2-title">{day.title}</div>
                  <div className="card-v2-practice">{day.practice}</div>

                  {/* State indicators */}
                  {complete && (
                    <div className="card-v2-status done-status">
                      <span className="card-v2-check">✓</span>
                      {t(city, { nyc: 'Complete', paris: 'Complète', london: 'Complete', milan: 'Completa' })}
                      {data?.grat_thanks && (
                        <span className="card-v2-thanks"> · {data.grat_thanks.filter(t2 => t2 >= 3).length * 3} ✦</span>
                      )}
                    </div>
                  )}
                  {isNext && (
                    <div className="card-v2-status next-status" style={{ color: cityData.primaryColor }}>
                      {t(city, { nyc: "Today's station →", paris: "Station du jour →", london: "Today's station →", milan: "Stazione di oggi →" })}
                    </div>
                  )}
                  {!complete && gratCount > 0 && !isNext && (
                    <div className="card-v2-status partial-status">{gratCount}/10 stops</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {toast && <div className="toast show">{toast}</div>}
    </div>
  )
}const CITY_PHOTOS = {
  nyc:    ['nyc1', 'nyc2', 'nyc3', 'nyc4', 'nyc5', 'nyc6', 'nyc7'],
  paris:  ['paris1', 'paris2', 'paris3', 'paris4', 'paris5', 'paris6', 'paris7'],
  london: ['london1', 'london2', 'london3', 'london4', 'london5', 'london6', 'london7'],
  milan:  ['milan1', 'milan2', 'milan3', 'milan4', 'milan5', 'milan6', 'milan7'],
  berlin: ['berlin1', 'berlin2', 'berlin3', 'berlin4', 'berlin5', 'berlin6', 'berlin7'],
  madrid: ['madrid1', 'madrid2', 'madrid3', 'madrid4', 'madrid5', 'madrid6', 'madrid7'],
}
const getCityPhotoForStation = (city, stationNum) => {
  const pool = CITY_PHOTOS[city] || CITY_PHOTOS.nyc
  const name = pool[(stationNum - 1) % pool.length]
  return 'https://magic.soappower.com/images/cities/' + name + '.jpg'
}
const getRandomCityPhoto = (city) => {
  const pool = CITY_PHOTOS[city] || CITY_PHOTOS.nyc
  const name = pool[Math.floor(Math.random() * pool.length)]
  return 'https://magic.soappower.com/images/cities/' + name + '.jpg'
}
